/**
 * Sections Shape de l'accueil (sections/shape-feature, -trusted, -story, -compare, -journal, -newsletter, -new-arrivals).
 * - Apparition en cascade au défilement ([data-reveal] → .is-visible)
 * - Vidéos : lecture seulement à l'écran + bouton pause / lecture
 * - Comparateur avant / après : curseur glissé (pointeur + clavier via un input range)
 * - Nouveautés : rail qui défile en continu (<shape-marquee>)
 * Chargé en module : exécuté une seule fois même si plusieurs sections l'incluent.
 */

const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

/* ---------- Apparition au défilement ---------- */

const revealObserver = new IntersectionObserver(
  (entries) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      entry.target.classList.add('is-visible');
      revealObserver.unobserve(entry.target);
    });
  },
  { threshold: 0.15, rootMargin: '0px 0px -40px 0px' }
);

function initReveal(root) {
  root.querySelectorAll('[data-reveal]:not(.is-visible)').forEach((element) => revealObserver.observe(element));
}

/* ---------- Vidéos ---------- */

const videoObserver = new IntersectionObserver(
  (entries) => {
    entries.forEach((entry) => {
      const video = entry.target;
      if (!(video instanceof HTMLVideoElement)) return;
      // Pas de relance si l'utilisateur a mis en pause
      if (entry.isIntersecting && !video.dataset.userPaused && !reducedMotion.matches) {
        video.play().catch(() => {});
      } else {
        video.pause();
      }
    });
  },
  { threshold: 0.2 }
);

function syncToggle(toggle, video) {
  const paused = video.paused;
  toggle.classList.toggle('is-paused', paused);
  toggle.setAttribute('aria-label', paused ? toggle.dataset.labelPlay || '' : toggle.dataset.labelPause || '');
}

function initVideos(root) {
  root.querySelectorAll('[data-shape-video]').forEach((wrapper) => {
    if (wrapper.dataset.ready) return;
    wrapper.dataset.ready = 'true';
    const video = wrapper.querySelector('video');
    if (!video) return;
    video.muted = true;
    video.removeAttribute('autoplay');
    videoObserver.observe(video);

    const toggle = wrapper.querySelector('[data-video-toggle]');
    if (!toggle) return;
    video.addEventListener('play', () => syncToggle(toggle, video));
    video.addEventListener('pause', () => syncToggle(toggle, video));
    toggle.addEventListener('click', () => {
      if (video.paused) {
        delete video.dataset.userPaused;
        video.play().catch(() => {});
      } else {
        video.dataset.userPaused = 'true';
        video.pause();
      }
    });
    syncToggle(toggle, video);
  });
}

/* ---------- Comparateur avant / après ---------- */

function initCompare(root) {
  root.querySelectorAll('[data-compare]').forEach((compare) => {
    if (!(compare instanceof HTMLElement) || compare.dataset.ready) return;
    compare.dataset.ready = 'true';
    const range = compare.querySelector('input[type="range"]');
    if (!(range instanceof HTMLInputElement)) return;

    const setPosition = (value) => {
      const clamped = Math.min(100, Math.max(0, value));
      compare.style.setProperty('--pos', `${clamped}%`);
      range.value = String(Math.round(clamped));
    };

    range.addEventListener('input', () => {
      compare.classList.remove('is-hinting');
      setPosition(Number(range.value));
    });

    // Glisser n'importe où sur l'image (plus confortable que viser la poignée)
    const fromPointer = (event) => {
      const rect = compare.getBoundingClientRect();
      setPosition(((event.clientX - rect.left) / rect.width) * 100);
    };
    compare.addEventListener('pointerdown', (event) => {
      if (event.button !== 0) return;
      compare.classList.remove('is-hinting');
      compare.classList.add('is-dragging');
      compare.setPointerCapture(event.pointerId);
      fromPointer(event);
    });
    compare.addEventListener('pointermove', (event) => {
      if (compare.classList.contains('is-dragging')) fromPointer(event);
    });
    const stop = () => compare.classList.remove('is-dragging');
    compare.addEventListener('pointerup', stop);
    compare.addEventListener('pointercancel', stop);

    setPosition(Number(range.value));

    // Petit mouvement d'invitation à la première apparition
    if (!reducedMotion.matches) {
      const hintObserver = new IntersectionObserver(
        (entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          hintObserver.disconnect();
          compare.classList.add('is-hinting');
          compare.addEventListener('animationend', () => compare.classList.remove('is-hinting'), { once: true });
        },
        { threshold: 0.5 }
      );
      hintObserver.observe(compare);
    }
  });
}

/* ---------- Rail qui défile en continu (sections/shape-new-arrivals) ---------- */

/**
 * <shape-marquee> : la liste [data-marquee-list] est dupliquée (copies masquées aux lecteurs d'écran)
 * pour boucler sans fin dans [data-marquee-track], qui reste un vrai conteneur défilant
 * (glisser au doigt / trackpad possible). Avance de data-speed px/s, seulement à l'écran ;
 * pause au survol, au focus et au toucher ; pas d'avance automatique si mouvement réduit.
 * Flèches [data-marquee-prev] / [data-marquee-next] de la section : une carte à la fois.
 */
class ShapeMarquee extends HTMLElement {
  /**
   * Carte clonée (inert) actuellement survolée à la main, via #syncClonedCardHover
   * @type {HTMLElement | null}
   */
  #hoveredClone = null;

  connectedCallback() {
    this.track = /** @type {HTMLElement | null} */ (this.querySelector('[data-marquee-track]'));
    this.list = /** @type {HTMLElement | null} */ (this.querySelector('[data-marquee-list]'));
    if (!this.track || !this.list || this.dataset.ready) return;
    this.dataset.ready = 'true';

    this.speed = Number(this.dataset.speed || 30);
    this.pos = 0;
    this.hovered = false;
    this.visible = false;
    this.holdUntil = 0;

    this.#fill();
    new ResizeObserver(() => this.#fill()).observe(this.track);

    // Défilement manuel (doigt, trackpad, flèches) : on reprend la position ; les écarts d'arrondi
    // de l'avance automatique (moins d'un pixel par image) sont ignorés
    this.track.addEventListener('scroll', () => {
      if (this.track && Math.abs(this.track.scrollLeft - this.pos) > 2) this.pos = this.track.scrollLeft;
    });
    this.track.addEventListener('scrollend', () => this.#wrap());

    const hold = () => {
      this.hovered = true;
    };
    const release = () => {
      this.hovered = false;
      this.holdUntil = performance.now() + 1500;
    };
    this.addEventListener('pointerenter', (event) => {
      if (event.pointerType === 'mouse') hold();
    });
    this.addEventListener('pointerleave', release);
    this.addEventListener('touchstart', hold, { passive: true });
    this.addEventListener('touchend', release, { passive: true });
    this.addEventListener('focusin', hold);
    this.addEventListener('focusout', release);

    // Les copies posées par #fill() pour boucler sans fin sont `inert` (pour ne pas doubler les
    // liens au clavier / lecteur d'écran), ce qui empêche aussi :hover d'y réagir à la souris :
    // on reproduit l'effet au survol à la main, en comparant la position du pointeur aux cartes.
    this.addEventListener('pointermove', (event) => {
      if (event.pointerType === 'mouse') this.#syncClonedCardHover(event.clientX, event.clientY);
    });
    this.addEventListener('pointerleave', () => this.#syncClonedCardHover(null, null));

    const section = this.closest('.sh-new');
    section?.querySelector('[data-marquee-prev]')?.addEventListener('click', () => this.#step(-1));
    section?.querySelector('[data-marquee-next]')?.addEventListener('click', () => this.#step(1));

    new IntersectionObserver((entries) => {
      this.visible = entries.some((entry) => entry.isIntersecting);
    }).observe(this);

    this.last = performance.now();
    requestAnimationFrame((time) => this.#tick(time));
  }

  /**
   * Pose `.is-hovered` (voir shape-home.css) sur la carte clonée sous (x, y), en comparant les
   * coordonnées aux cartes plutôt qu'en s'appuyant sur :hover / elementFromPoint, que `inert`
   * fait ignorer. Coordonnées nulles = pointeur sorti du rail.
   * @param {number | null} x
   * @param {number | null} y
   */
  #syncClonedCardHover(x, y) {
    let match = null;
    if (x !== null && y !== null && this.track) {
      for (const card of this.track.querySelectorAll('[data-marquee-copy] .sh-new-card')) {
        const rect = card.getBoundingClientRect();
        if (x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom) {
          match = card;
          break;
        }
      }
    }
    if (match === this.#hoveredClone) return;
    this.#hoveredClone?.classList.remove('is-hovered');
    match?.classList.add('is-hovered');
    this.#hoveredClone = match;
  }

  /** Ajoute des copies de la liste jusqu'à pouvoir boucler sur toute la largeur */
  #fill() {
    if (!this.track || !this.list) return;
    this.period = this.list.offsetWidth;
    if (!this.period) return;
    const needed = Math.ceil((this.track.clientWidth * 2) / this.period) + 1;
    let copies = this.track.querySelectorAll('[data-marquee-copy]').length;
    while (copies < needed) {
      const clone = /** @type {HTMLElement} */ (this.list.cloneNode(true));
      clone.removeAttribute('data-marquee-list');
      clone.setAttribute('data-marquee-copy', '');
      clone.setAttribute('aria-hidden', 'true');
      clone.inert = true;
      this.track.append(clone);
      copies += 1;
    }
  }

  /** Ramène la position dans la première période (saut invisible, le contenu est identique) */
  #wrap() {
    if (!this.track || !this.period) return;
    if (this.track.scrollLeft >= this.period) this.#jump(this.track.scrollLeft - this.period);
  }

  /** @param {number} left */
  #jump(left) {
    if (!this.track) return;
    this.pos = left;
    this.track.scrollLeft = left;
  }

  /** @param {number} direction */
  #step(direction) {
    if (!this.track || !this.list) return;
    const item = this.list.firstElementChild;
    const gap = parseFloat(getComputedStyle(this.list).columnGap) || 0;
    const distance = ((item instanceof HTMLElement ? item.offsetWidth : 250) + gap) * direction;
    // Clics rapprochés : on part de la cible précédente pour que les pas s'additionnent
    const now = performance.now();
    const base = this.stepTarget !== undefined && now < this.stepUntil ? this.stepTarget : this.track.scrollLeft;
    let target = base + distance;
    // Avant de reculer au tout début, on saute d'une période (contenu identique) pour avoir de la marge
    if (target < 0 && this.period) {
      this.#jump(this.track.scrollLeft + this.period);
      target += this.period;
    }
    this.stepTarget = target;
    this.stepUntil = now + 700;
    this.holdUntil = now + 2500;
    this.track.scrollTo({ left: target, behavior: reducedMotion.matches ? 'auto' : 'smooth' });
  }

  /** @param {number} time */
  #tick(time) {
    const delta = Math.min(time - (this.last || time), 64);
    this.last = time;
    const running = this.visible && !this.hovered && time > this.holdUntil && !reducedMotion.matches;
    if (running && this.track && this.period) {
      this.pos += (this.speed * delta) / 1000;
      if (this.pos >= this.period) this.pos -= this.period;
      this.track.scrollLeft = this.pos;
    }
    requestAnimationFrame((next) => this.#tick(next));
  }
}

if (!customElements.get('shape-marquee')) customElements.define('shape-marquee', ShapeMarquee);

/* ---------- Initialisation (+ rechargement de section dans l'éditeur) ---------- */

function init(root = document) {
  document.documentElement.classList.add('shape-home-ready');
  initReveal(root);
  initVideos(root);
  initCompare(root);
}

init();

document.addEventListener('shopify:section:load', (event) => {
  if (event.target instanceof Element) init(event.target);
});
